import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import moment, { Moment } from "moment";

import { Separator, SeparatorKey } from "./Separator/Separator";

import "./ThreadHistoryList.scss";

import { classNames } from "primereact/utils";

import { DateIs } from "../../../../utils/dateUtils";
import { translationKeys } from "../../../models/app-translation-keys";
import { ChatThreadExtendedModel } from "../../../models/interfaces";
import { useStore } from "../../../stores/StoreContext";
import { ThreadItem } from "./ThreadItem/ThreadItem";

interface IThreadHistoryList {
  threads: ChatThreadExtendedModel[];
  activeThreadId?: string;
  showDateSeparators?: boolean;
}

const RawThreadHistoryList: FunctionComponent<IThreadHistoryList> = (props: IThreadHistoryList) => {
  const { t } = useTranslation();
  const { userStore, threadStore } = useStore();

  const [sortedThreads, setSortedThreads] = useState<ChatThreadExtendedModel[]>([]);

  useEffect(() => {
    setSortedThreads(sortThreads(props.threads));
  }, [props.threads]);

  const dateChecksMap: Map<SeparatorKey, { used: boolean; func: (date: Moment) => boolean }> = new Map([
    ["today", { used: false, func: DateIs.today }],
    ["yesterday", { used: false, func: DateIs.yesterday }],
    ["lastWeek", { used: false, func: DateIs.lastWeek }],
    ["p30Days", { used: false, func: DateIs.previous30Days }],
    ["currentYear", { used: false, func: DateIs.currentYear }],
    ["beforeCurrentYear", { used: false, func: DateIs.beforeCurrentYear }],
  ]);

  const sortThreads = (threads: ChatThreadExtendedModel[]) => {
    return threads.sort((a, b) => {
      const dateA = a.lastActivity ? a.lastActivity.valueOf() : 0;
      const dateB = b.lastActivity ? b.lastActivity.valueOf() : 0;
      return dateB - dateA;
    });
  };

  const generateSeparatorMemo = useMemo(
    () =>
      (thread: ChatThreadExtendedModel): SeparatorKey | undefined => {
        const momThreadDate = moment(thread.lastActivity);
        for (const [key, dateCheck] of dateChecksMap) {
          if (!dateCheck.used) {
            if (dateCheck.func(momThreadDate)) {
              dateCheck.used = true;
              return key;
            }
          }
        }
        return undefined;
      },
    [dateChecksMap, userStore.currentLanguage],
  );

  const separatorTemplate = (thread: ChatThreadExtendedModel, index: number) => {
    const separatorKey = generateSeparatorMemo(thread);

    return (
      !!separatorKey && (
        <li className="separator" key={"separator-" + index + "-" + separatorKey}>
          <Separator separatorKey={separatorKey} />
        </li>
      )
    );
  };

  const threadItemTemplate = (thread: ChatThreadExtendedModel) => {
    const isActive = thread.id === props.activeThreadId;

    return (
      <li className={classNames("thread", { active: isActive })} key={thread.id}>
        <ThreadItem thread={thread} isActive={isActive} />
      </li>
    );
  };

  if (!sortedThreads.length) {
    return <span className="no-threads">{t(translationKeys.sidebarThreads.noThreads)}</span>;
  }

  return (
    <ul className="thread-list">
      {sortedThreads.map((thread, index) => {
        const updatedThread = threadStore.threads.find(t => t.id === thread.id) || thread;

        return [props.showDateSeparators && separatorTemplate(updatedThread, index), threadItemTemplate(updatedThread)];
      })}
    </ul>
  );
};

export const ThreadHistoryList = observer(RawThreadHistoryList);
